/**
 * Capability flags for Task Detail header actions (no persona switcher).
 * Mirrors assignment checks from TaskHeader.filteredUserTypeOptions.
 */
import { isAwaitingSignOff } from './tasklistHelpers';

export function getTaskCapabilities({ task, taskListData, userId, roleOptions = ['manager', 'preparer', 'approver'] }) {
  const caps = {
    preparer: false,
    approver: false,
    manager: false,
  };

  if (!task || !userId) {
    return caps;
  }

  const allowed = [];

  if (Array.isArray(task.preparer) && task.preparer.includes(userId)) {
    allowed.push('preparer');
  } else if (typeof task.preparer === 'string' && task.preparer === userId) {
    allowed.push('preparer');
  }

  if (Array.isArray(task.approver) && task.approver.includes(userId)) {
    allowed.push('approver');
  } else if (typeof task.approver === 'string' && task.approver === userId) {
    allowed.push('approver');
  }

  const signOffIds = Array.isArray(task.sign_off_approvers) ? task.sign_off_approvers : [];
  if (signOffIds.includes(userId)) {
    allowed.push('approver');
  }

  const managerList = Array.isArray(taskListData?.metadata?.managerlist)
    ? taskListData.metadata.managerlist
    : [];

  if (
    (Array.isArray(task.manager) && task.manager.includes(userId)) ||
    (typeof task.manager === 'string' && task.manager === userId) ||
    managerList.includes(userId)
  ) {
    allowed.push('manager');
  }

  let filtered = allowed;
  if (Array.isArray(task.allowedRoles) && task.allowedRoles.length > 0) {
    filtered = allowed.filter((role) => task.allowedRoles.includes(role));
  }

  if (filtered.length === 0) {
    filtered = [...roleOptions];
  }

  filtered.forEach((role) => {
    if (role in caps) {
      caps[role] = true;
    }
  });

  return caps;
}

/** Task is closed / approved — disable most actions. */
export function isTaskClosedForActions(task) {
  if (!task) return true;
  const statusdetail = typeof task.statusdetail === 'string' ? task.statusdetail.toLowerCase() : '';
  return task.status === false || statusdetail.includes('approve') || task.stage === 3;
}

/**
 * Resolve workflow action capabilities — prefers API `task.capabilities`, falls back to legacy.
 */
export function resolveTaskActionCapabilities(opts) {
  const { task } = opts;
  const api = task?.capabilities;
  const legacy = getTaskCapabilities(opts);

  const awaitingL2 = isAwaitingSignOff(task);
  const closed = isTaskClosedForActions(task);
  const preparerCanUploadOrSubmit = legacy.preparer && !closed && !awaitingL2;

  if (api && typeof api === 'object' && Object.keys(api).length > 0) {
    const can_l1_approve = !!api.can_l1_approve;
    const can_sign_off = !!api.can_sign_off;
    const can_reject = !!(api.can_reject || can_l1_approve || can_sign_off);
    // Stage 2 is already submitted. Trust the API flag so Submit does not stay up.
    const can_submit = Object.prototype.hasOwnProperty.call(api, 'can_submit')
      ? !!api.can_submit
      : preparerCanUploadOrSubmit;

    return {
      preparer: !!api.can_submit,
      approver: !!(can_l1_approve || can_sign_off || can_reject || api.can_return_to_pending),
      manager: legacy.manager || !!api.can_edit,
      can_submit,
      can_l1_approve,
      can_sign_off,
      can_reject,
      can_return_to_pending: !!api.can_return_to_pending,
      can_edit: !!api.can_edit,
    };
  }

  const approvalState = task?.approval_state || {};
  const can_l1_approve = legacy.approver && task?.stage === 2 && !approvalState.l1_completed && !closed;
  const can_sign_off = (legacy.approver || legacy.manager) && awaitingL2 && !closed;
  const can_reject = !closed && legacy.approver && task?.stage !== 0;

  return {
    ...legacy,
    can_submit: preparerCanUploadOrSubmit,
    can_l1_approve,
    can_sign_off,
    can_reject,
    can_return_to_pending: false,
    can_edit: legacy.manager,
  };
}
